/**
 * Enough of an XDIS91 to be the parent of an XBIN01.
 *
 * Element order and fixed codes are copied from MedCom's
 * `XDIS91_Eks_Med bilag.xml` (namespace 2006/07/01).
 * The parent BIN reference uses the same object id as the MEDBIN, with the hyphens removed.
 * The letter body is not invented. This is not the DIS91 certification protocol.
 */

import type { MedbinParty } from "./types";
import { parentUuidWithoutHyphens } from "./xbin";

export const XDIS91_NS = "http://rep.oio.dk/sundcom.dk/medcom.dk/xml/schemas/2006/07/01/";

export type Xdis91ParentInput = {
  envelopeIdentifier: string;
  letterIdentifier: string;
  sentDate: string;
  sentTime: string;
  authorisationDate: string;
  authorisationTime: string;
  sender: MedbinParty;
  receiver: MedbinParty;
  patientCpr: string;
  patientSurname: string;
  patientGivenName: string;
  /** UUID with hyphens, same value as the MEDBIN ObjectIdentifier. */
  objectIdentifier: string;
  objectCode: string;
  objectExtensionCode: string;
  originalObjectSize: number;
};

function assertLatin1(value: string, field: string): void {
  for (let i = 0; i < value.length; i++) {
    if (value.charCodeAt(i) > 255) {
      throw new Error(`${field} is not ISO-8859-1`);
    }
  }
}

function escapeText(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

function required(tag: string, value: string): string {
  assertLatin1(value, tag);
  return `<${tag}>${escapeText(value)}</${tag}>`;
}

function optional(tag: string, value: string | undefined): string {
  if (value === undefined || value === "") return "";
  return required(tag, value);
}

function partyXml(tag: "Sender" | "Receiver", party: MedbinParty): string {
  return [
    `<${tag}>`,
    required("EANIdentifier", party.ean),
    required("Identifier", party.identifier),
    required("IdentifierCode", party.identifierCode),
    required("OrganisationName", party.organisationName),
    optional("DepartmentName", party.departmentName),
    optional("UnitName", party.unitName),
    optional("StreetName", party.streetName),
    optional("DistrictName", party.districtName),
    optional("PostCodeIdentifier", party.postCodeIdentifier),
    tag === "Sender" ? optional("MedicalSpecialityCode", party.medicalSpecialityCode) : "",
    `</${tag}>`,
  ].filter((line) => line !== "").join("");
}

/** Parent correspondence. The BIN object id has no hyphens. */
export function buildXdis91Parent(input: Xdis91ParentInput): string {
  const objectId = parentUuidWithoutHyphens(input.objectIdentifier);
  if (objectId.length === 0 || objectId.includes("-")) {
    throw new Error("Parent object id must be the MEDBIN id without hyphens");
  }
  return [
    `<?xml version="1.0" encoding="iso-8859-1"?>`,
    `<Emessage xmlns="${XDIS91_NS}">`,
    `<Envelope>`,
    `<Sent>`,
    required("Date", input.sentDate),
    required("Time", input.sentTime),
    `</Sent>`,
    required("Identifier", input.envelopeIdentifier),
    required("AcknowledgementCode", "pluspositivkvitt"),
    `</Envelope>`,
    `<ClinicalEmail>`,
    `<Letter>`,
    required("Identifier", input.letterIdentifier),
    required("VersionCode", "XD9134L"),
    required("StatisticalCode", "XDIS91"),
    `<Authorisation>`,
    required("Date", input.authorisationDate),
    required("Time", input.authorisationTime),
    `</Authorisation>`,
    required("TypeCode", "XDIS91"),
    required("StatusCode", "nytbrev"),
    `</Letter>`,
    partyXml("Sender", input.sender),
    partyXml("Receiver", input.receiver),
    `<Patient>`,
    required("CivilRegistrationNumber", input.patientCpr),
    required("PersonSurnameName", input.patientSurname),
    required("PersonGivenName", input.patientGivenName),
    `</Patient>`,
    `<Reference>`,
    required("RefDescription", "BIN"),
    `<BIN>`,
    required("ObjectIdentifier", objectId),
    required("ObjectCode", input.objectCode),
    required("ObjectExtensionCode", input.objectExtensionCode),
    required("OriginalObjectSize", String(input.originalObjectSize)),
    `</BIN>`,
    `</Reference>`,
    `</ClinicalEmail>`,
    `</Emessage>`,
  ].join("");
}
